<template>
  <section
    v-if="visible"
    class="changement"
    :class="`changement--${changement.mode}`"
    :aria-labelledby="idTitre"
  >
    <h5 :id="idTitre" class="changement-titre">
      {{ changement.titre }}
    </h5>

    <div class="changement-corps">
      <template v-for="(segment, index) in changement.segments" :key="index">
        <span v-if="segment.sep" class="sep" />
        <span v-else-if="segment.head" class="localisation">{{
          segment.t
        }}</span>
        <template v-else>
          <del v-if="segment.del" class="supprime">{{ segment.t }}</del>
          <ins v-else-if="segment.ins" class="ajoute">{{ segment.t }}</ins>
          <template v-else>{{ segment.t }}</template>
          <template v-if="!segment.glue">{{ " " }}</template>
        </template>
      </template>
    </div>

    <div v-if="aSupprime || aAjoute" class="legende">
      <span v-if="aSupprime" class="supprime">Supprimé</span>
      <span v-if="aAjoute" class="ajoute">Ajouté</span>
    </div>

    <p v-if="changement.autres > 0" class="suite">
      + {{ changement.autres }} autre{{
        changement.autres > 1 ? "s" : ""
      }}
      modification{{ changement.autres > 1 ? "s" : "" }}, à lire dans le
      dispositif complet
    </p>
    <button
      v-if="changement.tronque || changement.autres > 0"
      type="button"
      class="voir-dispositif"
      @click="voirLeDispositif"
    >
      Voir le dispositif complet
    </button>
  </section>
</template>

<script setup>
import { computed, useId } from "vue";

const props = defineProps({
  // Résultat du lecteur de dispositif (champ `changement` du zoom amendement).
  changement: {
    type: Object,
    default: null,
  },
  // Identifiant de la zone du dispositif complet, vers laquelle « Voir le dispositif complet » fait défiler.
  cibleDispositif: {
    type: String,
    default: "amendement-dispositif",
  },
});

// Un identifiant par instance : plusieurs fiches peuvent être montées sur la même page.
const idTitre = `changement-titre-${useId()}`;

const segments = computed(() => props.changement?.segments ?? []);
const aSupprime = computed(() => segments.value.some((s) => s.del));
const aAjoute = computed(() => segments.value.some((s) => s.ins));
// Le bloc n'apparaît que s'il apporte du barré ou de l'ajouté : un dispositif en texte simple est déjà affiché
// juste en dessous (« Amendement : »).
const visible = computed(() => aSupprime.value || aAjoute.value);

const voirLeDispositif = () => {
  const cible = document.getElementById(props.cibleDispositif);
  if (!cible) return;
  cible.scrollIntoView({ behavior: "smooth", block: "start" });
  cible.focus({ preventScroll: true });
};
</script>

<style lang="scss" scoped>
.changement {
  background-color: #fbdef0;
  border-left: 4px solid var(--clr-service-amendment, #de018a);
  border-radius: 12px;
  padding: 16px 16px 14px 16px;
  color: var(--clr-n8, #1b0e28);
}

.changement-titre {
  margin: 0 0 10px 0;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
}

.changement-corps {
  background-color: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 0.9375rem;
  line-height: 1.7;
  overflow-wrap: anywhere;
  word-break: break-word;

  .sep {
    display: block;
    height: 0.8em;
  }

  .localisation {
    display: block;
    padding-bottom: 4px;
    font-size: 0.8125rem;
    font-weight: 700;
    line-height: 1.4;
    color: var(--color-neutral-7, #4f4559);
  }
}

.supprime {
  background-color: var(--color-error-50, #fae7e7);
  color: var(--color-error-400, #8e0a0a);
  text-decoration: line-through;
  border-radius: 3px;
  padding: 0 3px;
}

.ajoute {
  background-color: var(--color-success-50, #e6fae6);
  color: var(--color-success-500, #007c00);
  text-decoration: none;
  font-weight: 700;
  border-radius: 3px;
  padding: 0 3px;
}

.legende {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 10px;
  font-size: 0.78rem;

  span {
    padding: 0 5px;
  }
}

.suite {
  margin: 8px 0 0 0;
  font-size: 0.8125rem;
  line-height: 1.5;
}

.voir-dispositif {
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.5;
  color: var(--clr-n8, #1b0e28);
  text-decoration: underline;
  cursor: pointer;
}
</style>
